import { useField, useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';

import { InputWrapper } from 'components/InputWrapper';
import { InlineSelect } from 'components/select/InlineSelect';
import { INVOICE_CHANNEL_OPTIONS, INVOICE_CHANNELS, type InvoiceChannel } from '../../document-delivery';

export interface IInvoiceChannelsFieldProps {
  name?: string;
  labelText?: string;
  helperText?: string;
  /**
   * With `defaultChannelsForCountry`, resets the channels to the country default whenever
   * the country changes (create forms). Omitted on edit forms, which keep the saved value.
   */
  country?: string;
  defaultChannelsForCountry?: (country: string | undefined) => InvoiceChannel[];
}

/** Email/Peppol send channels; shared by customer invoices and carrier self-billing. */
export const InvoiceChannelsField: React.FC<IInvoiceChannelsFieldProps> = ({
  name = 'invoiceChannels',
  labelText = 'Factuur verzenden via',
  helperText = 'Altijd beschikbaar in het klantenportaal.',
  country,
  defaultChannelsForCountry,
}) => {
  const [field, meta, helpers] = useField<InvoiceChannel[]>({ name });
  const channels = field.value ?? [];

  // The country is set by another field (postal code autocomplete), so there is no
  // event handler here to hook into; follow the value instead.
  // `setFieldValue` is stable; the per-field `helpers.setValue` is recreated every render.
  const { setFieldValue } = useFormikContext();
  const prevCountryRef = useRef(country);
  useEffect(() => {
    if (defaultChannelsForCountry && country !== prevCountryRef.current) {
      prevCountryRef.current = country;
      setFieldValue(name, defaultChannelsForCountry(country));
    }
  }, [country, defaultChannelsForCountry, name, setFieldValue]);

  return (
    <InputWrapper
      labelText={labelText}
      helperText={helperText}
      invalidText={meta.touched ? meta.error : undefined}
      noLabel
    >
      <InlineSelect
        items={INVOICE_CHANNEL_OPTIONS}
        selectedItems={INVOICE_CHANNEL_OPTIONS.filter((option) => channels.includes(option.key))}
        onSelect={(selected) => {
          helpers.setValue(INVOICE_CHANNELS.filter((channel) => selected.some((item) => item.key === channel)));
          helpers.setTouched(true);
        }}
      />
    </InputWrapper>
  );
};
